/**
 * Correo — la pantalla nueva. Tres pasos: a quién, qué dice, cuándo.
 *
 * POR QUE EMPIEZA POR LA AUDIENCIA
 * La vieja empezaba por el diseño y pedía las credenciales del proveedor en
 * medio del constructor. Un vendedor no piensa "voy a hacer un HTML": piensa
 * "quiero escribirle a los que no me contestaron".
 *
 * TRES COSAS QUE LA VIEJA NO TENÍA
 *  - Un contador en vivo: cuántos van a recibir, cuántos se dieron de baja y
 *    cuántos estaban repetidos, ANTES de mandar.
 *  - Bloques en vez de HTML crudo. El HTML sigue disponible, pero escondido:
 *    mostrarle <table cellpadding='0'> a un vendedor no es "edición fácil".
 *  - Resultados. Antes no había dónde ver qué pasó con lo que mandaste.
 *
 * Los filtros salen de los datos DE ESA EMPRESA con sus contadores, no de una
 * lista inventada: si un tenant nunca usó Facebook, no ve "facebook".
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import HelpTip from '@/components/HelpTip';
import {
  Users, PenLine, Send, BarChart3, Loader2, Plus, Trash2, Eye,
  Smartphone, Monitor, CheckCircle2, AlertTriangle, Mail, Upload, Clock, FileCode, Save, Zap, ChevronUp, ChevronDown,
} from 'lucide-react';

const ETIQUETA_ESTADO = {
  nuevo: 'Nuevo', prospecto: 'Prospecto', contactado: 'Contactado',
  calificado: 'Calificado', negociacion: 'En negociación',
  ganado: 'Ganado', perdido: 'Perdido',
};
const ETIQUETA_ORIGEN = {
  whatsapp_ai: 'WhatsApp con IA', whatsapp: 'WhatsApp', facebook: 'Facebook',
  manual: 'Capturado a mano', website: 'Sitio web', landing: 'Landing page',
};

const PLANTILLAS = [
  {
    id: 'showcase', nombre: 'Muestra de producto', pie: 'Para presentar 1 propiedad o modelo',
    bloques: [
      { tipo: 'titulo', texto: 'Esto puede ser tuyo' },
      { tipo: 'imagen', url: '' },
      { tipo: 'texto', texto: 'Cuéntale en dos líneas por qué le conviene. Sin adornos: qué es, dónde está y qué gana.' },
      { tipo: 'boton', texto: 'Quiero más información', url: '' },
    ],
  },
  {
    id: 'seguimiento', nombre: 'Seguimiento', pie: 'Para quien no contestó',
    bloques: [
      { tipo: 'titulo', texto: '¿Seguimos donde lo dejamos?' },
      { tipo: 'texto', texto: 'Te escribimos hace unos días y no supimos de ti. Si aún te interesa, responde este correo y te atiendo yo mismo.' },
      { tipo: 'boton', texto: 'Retomar mi solicitud', url: '' },
    ],
  },
  {
    id: 'novedad', nombre: 'Novedad', pie: 'Para anunciar algo nuevo',
    bloques: [
      { tipo: 'titulo', texto: 'Tenemos algo nuevo' },
      { tipo: 'texto', texto: 'Escribe aquí la novedad. Una idea por correo: el que anuncia tres cosas no anuncia ninguna.' },
      { tipo: 'imagen', url: '' },
      { tipo: 'boton', texto: 'Ver más', url: '' },
    ],
  },
  {
    id: 'bienvenida', nombre: 'Bienvenida', pie: 'Para saludar a un contacto nuevo',
    bloques: [
      { tipo: 'titulo', texto: '¡Bienvenido!' },
      { tipo: 'texto', texto: 'Gracias por tu interés. Esto es lo que sigue y cómo te ayudamos.' },
      { tipo: 'imagen', url: '' },
      { tipo: 'boton', texto: 'Comenzar', url: '' },
    ],
  },
  {
    id: 'promo', nombre: 'Promoción', pie: 'Para una oferta con fecha límite',
    bloques: [
      { tipo: 'titulo', texto: 'Solo por esta semana' },
      { tipo: 'imagen', url: '' },
      { tipo: 'texto', texto: 'Describe la oferta en una línea: qué incluye y hasta cuándo.' },
      { tipo: 'boton', texto: 'Aprovechar ahora', url: '' },
    ],
  },
  {
    id: 'video_novedad', nombre: 'Con video', pie: 'Para mostrar un video de YouTube',
    bloques: [
      { tipo: 'titulo', texto: 'Míralo en 1 minuto' },
      { tipo: 'video', url: '' },
      { tipo: 'texto', texto: 'Una línea que invite a ver el video y a dar el siguiente paso.' },
      { tipo: 'boton', texto: 'Quiero saber más', url: '' },
    ],
  },
];

function youtubeId(url) {
  const m = String(url || '').match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : '';
}

// Endurece el href para el PREVIEW en la app (el envío ya pasa por sanear() en el
// backend, que neutraliza javascript:; esto cubre además el preview, que no). No
// toca #magic-ID ni URLs normales; solo neutraliza esquemas ejecutables.
function hrefSeguro(u) {
  const s = String(u || '').trim();
  return /^(javascript|data|vbscript):/i.test(s) ? '#' : s;
}

function construirHtml(bloques, fuente) {
  const ff = fuente || 'Arial, Helvetica, sans-serif';
  const partes = (bloques || []).map(b => {
    if (b.tipo === 'titulo') {
      return `<h1 style="margin:0 0 16px;font-size:24px;line-height:1.3;color:#111827;font-family:${ff}">${b.texto || ''}</h1>`;
    }
    if (b.tipo === 'texto') {
      return `<p style="margin:0 0 16px;font-size:15px;line-height:1.6;color:#374151;font-family:${ff}">${(b.texto || '').replace(/\n/g, '<br/>')}</p>`;
    }
    if (b.tipo === 'imagen' && b.url) {
      const zonas = Array.isArray(b.hotspots)
        ? b.hotspots.filter(h => h && h.destino && h.w > 0 && h.h > 0)
        : [];
      if (zonas.length) {
        const areas = zonas.map(h =>
          `<a href="${hrefSeguro(h.destino)}" target="_blank" style="position:absolute;left:${h.x}%;top:${h.y}%;width:${h.w}%;height:${h.h}%;text-decoration:none"></a>`
        ).join('');
        return `<div style="position:relative;display:block;max-width:560px;margin:0 0 16px"><img src="${b.url}" alt="" style="display:block;width:100%;border-radius:10px"/>${areas}</div>`;
      }
      const img = `<img src="${b.url}" alt="" style="display:block;width:100%;max-width:560px;border-radius:10px;margin:0 0 16px"/>`;
      return b.destino ? `<a href="${hrefSeguro(b.destino)}" target="_blank" style="text-decoration:none">${img}</a>` : img;
    }
    if (b.tipo === 'video') {
      const id = youtubeId(b.url);
      if (!id) return '';
      return `<a href="https://www.youtube.com/watch?v=${id}" target="_blank" style="display:block;position:relative;max-width:560px;margin:0 0 16px"><img src="https://img.youtube.com/vi/${id}/hqdefault.jpg" alt="Ver video" style="display:block;width:100%;border-radius:10px"/><span style="position:absolute;top:50%;left:50%;margin:-23px 0 0 -33px;width:66px;height:46px;background:rgba(0,0,0,.72);border-radius:12px;color:#fff;font-size:22px;text-align:center;line-height:46px">&#9654;</span></a>`;
    }
    if (b.tipo === 'magic') {
      return `<a href="#magic-${b.accion_id || ''}" style="display:inline-block;padding:13px 24px;border-radius:8px;background:#059669;color:#fff;text-decoration:none;font-size:15px;font-family:${ff};margin:0 0 16px;font-weight:600">${b.texto || 'Quiero que me contacten'}</a>`;
    }
    if (b.tipo === 'boton' && (b.texto || b.url)) {
      return `<a href="${b.url || '#'}" style="display:inline-block;padding:13px 22px;border-radius:8px;background:#2563eb;color:#fff;text-decoration:none;font-size:15px;font-family:${ff};margin:0 0 16px">${b.texto || 'Ver más'}</a>`;
    }
    return '';
  });
  return `<div style="background:#f3f4f6;padding:28px 0"><div style="max-width:600px;margin:0 auto;background:#fff;border-radius:14px;padding:32px;font-family:${ff}">${partes.join('')}</div></div>`;
}

// Hotspot: el usuario ARRASTRA el mouse sobre la imagen para dibujar una zona
// clicable; se guarda x/y/ancho/alto en PORCENTAJE (no px) para que escale igual
// en el correo. Cada zona lleva a una URL o a un botón mágico (#magic-ID, que el
// backend reemplaza por el enlace firmado al enviar). Nota: los overlays usan
// position:absolute; se ven bien en el preview y en clientes modernos (algunos
// webmail los recortan, por eso es "además del" enlace de imagen completa).
function HotspotEditor({ url, hotspots, onChange, magicAcciones }) {
  const ref = useRef(null);
  const ini = useRef(null);
  const [draw, setDraw] = useState(null);
  const zonas = Array.isArray(hotspots) ? hotspots : [];
  const r1 = (n) => Math.round(n * 10) / 10;

  const aPct = (e) => {
    const box = ref.current && ref.current.getBoundingClientRect();
    if (!box || !box.width || !box.height) return { x: 0, y: 0 };
    return {
      x: Math.min(100, Math.max(0, ((e.clientX - box.left) / box.width) * 100)),
      y: Math.min(100, Math.max(0, ((e.clientY - box.top) / box.height) * 100)),
    };
  };
  const onDown = (e) => { e.preventDefault(); const p = aPct(e); ini.current = p; setDraw({ x: p.x, y: p.y, w: 0, h: 0 }); };
  const onMove = (e) => {
    if (!ini.current) return;
    const p = aPct(e); const s = ini.current;
    setDraw({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
  };
  const onUp = () => {
    const d = draw; ini.current = null;
    if (d && d.w >= 3 && d.h >= 3) onChange([...zonas, { x: r1(d.x), y: r1(d.y), w: r1(d.w), h: r1(d.h), destino: '' }]);
    setDraw(null);
  };
  const cambiar = (i, val) => onChange(zonas.map((z, j) => (j === i ? { ...z, destino: val } : z)));
  const quitar = (i) => onChange(zonas.filter((_, j) => j !== i));

  return (
    <div className="space-y-1.5">
      <p className="text-[10px] text-muted-foreground">Arrastra sobre la imagen para dibujar un botón en una zona. Cada zona lleva a una URL o a un botón mágico.</p>
      <div ref={ref} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        className="relative inline-block select-none cursor-crosshair rounded-md overflow-hidden border border-white/10" style={{ maxWidth: '100%' }}>
        <img src={url} alt="" draggable={false} className="block w-full pointer-events-none" style={{ maxWidth: 360 }} />
        {zonas.map((z, i) => (
          <div key={i} className="absolute border-2 border-sky-400 bg-sky-400/20"
            style={{ left: `${z.x}%`, top: `${z.y}%`, width: `${z.w}%`, height: `${z.h}%` }}>
            <span className="absolute -top-4 left-0 text-[9px] bg-sky-500 text-white px-1 rounded">{i + 1}</span>
          </div>
        ))}
        {draw && (
          <div className="absolute border-2 border-dashed border-amber-300 bg-amber-300/20"
            style={{ left: `${draw.x}%`, top: `${draw.y}%`, width: `${draw.w}%`, height: `${draw.h}%` }} />
        )}
      </div>
      {zonas.length > 0 && (
        <div className="space-y-1.5">
          {zonas.map((z, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className="text-[10px] text-sky-300 w-4 shrink-0 text-center">{i + 1}</span>
              <Input value={z.destino || ''} placeholder="URL (https://...) o elige un botón mágico →"
                onChange={e => cambiar(i, e.target.value)} className="bg-black/20 border-white/10 text-xs flex-1" />
              <select value={(z.destino || '').startsWith('#magic-') ? z.destino.slice(7) : ''}
                onChange={e => cambiar(i, e.target.value ? `#magic-${e.target.value}` : '')}
                className="bg-black/20 border border-white/10 rounded-md text-[11px] px-1.5 py-2 text-white shrink-0" style={{ maxWidth: 120 }}>
                <option value="">botón mágico</option>
                {(magicAcciones || []).map(ac => <option key={ac.id} value={ac.id}>{ac.nombre}</option>)}
              </select>
              <button type="button" onClick={() => quitar(i)} className="text-muted-foreground hover:text-rose-400 shrink-0"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CorreoCampanasPage() {
  const [paso, setPaso] = useState('audiencia');
  const [opciones, setOpciones] = useState(null);
  const [filtros, setFiltros] = useState({ status: [], source: [] });
  const [conteo, setConteo] = useState(null);
  const [contando, setContando] = useState(false);

  const [asunto, setAsunto] = useState('');
  const [bloques, setBloques] = useState(PLANTILLAS[0].bloques);
  const [fuente, setFuente] = useState('Arial, Helvetica, sans-serif');
  const [avanzado, setAvanzado] = useState(false);
  const [htmlCrudo, setHtmlCrudo] = useState('');
  const [vista, setVista] = useState('escritorio');

  const [enviando, setEnviando] = useState(false);
  const [correoPrueba, setCorreoPrueba] = useState('');
  const [envios, setEnvios] = useState([]);
  const [listas, setListas] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [resumenCsv, setResumenCsv] = useState(null);
  const [programar, setProgramar] = useState(false);
  const [cuando, setCuando] = useState('');
  const [htmlPropio, setHtmlPropio] = useState('');
  const [enlaces, setEnlaces] = useState([]);
  const [destinos, setDestinos] = useState({});
  const [avisoHtml, setAvisoHtml] = useState('');
  const [plantillas, setPlantillas] = useState([]);
  const [topePlantillas, setTopePlantillas] = useState(10);
  const [nombrePlantilla, setNombrePlantilla] = useState('');
  const [automatismos, setAutomatismos] = useState(null);
  const [pasosSugeridos, setPasosSugeridos] = useState([]);
  const [etapas, setEtapas] = useState([]);  // Fase 3: etapas del pipeline para anclar nurturing
  const [correoListo, setCorreoListo] = useState(null);
  // Pieza A (D6/multidominio): remitente por marca/desarrollo del mailing.
  const [remitenteSel, setRemitenteSel] = useState('empresa');  // 'empresa' | 'brand:ID' | 'development:ID'
  const [licencias, setLicencias] = useState([]);
  const [domEmpresa, setDomEmpresa] = useState(null);
  const [domSel, setDomSel] = useState(null);

  useEffect(() => {
    api.get('/correo/audiencia/opciones').then(r => { setOpciones(r.data); setListas(r.data?.listas || []); }).catch(() => {});
    api.get('/correo/config').then(r => setCorreoListo(r.data?.configurado && r.data?.activo)).catch(() => setCorreoListo(false));
    api.get('/correo/envios').then(r => setEnvios(r.data?.envios || [])).catch(() => {});
    api.get('/correo/plantillas').then(r => { setPlantillas(r.data?.plantillas || []); setTopePlantillas(r.data?.tope || 10); }).catch(() => {});
    api.get('/correo/automatismos').then(r => { setAutomatismos(r.data?.automatismos || null); setPasosSugeridos(r.data?.pasos_sugeridos || []); }).catch(() => {});
    api.get('/pipeline').then(r => setEtapas(Array.isArray(r.data) ? r.data : (r.data?.stages || []))).catch(() => setEtapas([]));
  }, []);

  // Pieza A: marcas/desarrollos para el selector de remitente + dominio de la empresa.
  useEffect(() => {
    Promise.all([
      api.get('/brands').catch(() => ({ data: [] })),
      api.get('/developments').catch(() => ({ data: [] })),
    ]).then(([b, d]) => {
      const lista = (x) => (Array.isArray(x) ? x : (x?.brands || x?.developments || x?.items || []));
      const bs = lista(b.data).map(x => ({ entity_type: 'brand', entity_id: x.id, nombre: x.name || x.nombre || 'Marca' }));
      const ds = lista(d.data).map(x => ({ entity_type: 'development', entity_id: x.id, nombre: x.name || x.nombre || 'Desarrollo' }));
      setLicencias([...bs, ...ds].filter(l => l.entity_id));
    }).catch(() => {});
    api.get('/correo/dominio').then(r => setDomEmpresa(r.data || null)).catch(() => {});
  }, []);

  // Pieza A: resuelve el dominio de la marca/desarrollo elegido (para el letrero).
  useEffect(() => {
    if (remitenteSel === 'empresa') { setDomSel(null); return; }
    const [et, eid] = remitenteSel.split(':');
    if (!et || !eid) { setDomSel(null); return; }
    api.get(`/correo/dominio/licencia?entity_type=${encodeURIComponent(et)}&entity_id=${encodeURIComponent(eid)}`)
      .then(r => setDomSel(r.data || null)).catch(() => setDomSel(null));
  }, [remitenteSel]);

  useEffect(() => {
    setContando(true);
    const t = setTimeout(() => {
      api.post('/correo/audiencia/contar', filtros)
        .then(r => setConteo(r.data)).catch(() => {}).finally(() => setContando(false));
    }, 350);
    return () => clearTimeout(t);
  }, [filtros]);

  const html = useMemo(() => {
    if (htmlPropio) return htmlPropio;
    return avanzado && htmlCrudo ? htmlCrudo : construirHtml(bloques, fuente);
  }, [htmlPropio, avanzado, htmlCrudo, bloques, fuente]);

  const alternar = (campo, valor) => setFiltros(f => {
    const actual = f[campo] || [];
    return { ...f, [campo]: actual.includes(valor) ? actual.filter(v => v !== valor) : [...actual, valor] };
  });

  // El archivo se lee EN EL NAVEGADOR y se manda su texto. Asi no toca el disco
  // del servidor en ningun momento -- que es justo lo que se pidio -- y no hace
  // falta una dependencia mas solo para subir archivos.
  const cargarCsv = (archivo) => {
    if (!archivo) return;
    if (archivo.size > 6 * 1024 * 1024) {
      toast.error('El archivo pesa mas de 6 MB. Partelo en varios.');
      return;
    }
    setSubiendo(true);
    const lector = new FileReader();
    lector.onload = () => {
      api.post('/correo/audiencia/csv', { contenido: lector.result, nombre: archivo.name })
        .then(r => {
          setResumenCsv(r.data);
          setFiltros({ lista_id: r.data.lista_id });
          toast.success(`Lista lista: ${r.data.resumen.limpios} contactos limpios`);
          api.get('/correo/audiencia/opciones').then(x => setListas(x.data?.listas || []));
        })
        .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo leer el archivo'))
        .finally(() => setSubiendo(false));
    };
    lector.onerror = () => { setSubiendo(false); toast.error('No se pudo leer el archivo'); };
    lector.readAsText(archivo, 'UTF-8');
  };

  // El HTML del tenant se analiza en el servidor: ahi se sanea (fuera <script>,
  // <iframe>, atributos on* y enlaces javascript:) y se marca cada enlace para
  // poder cambiarle el destino sin adivinar cual era cual. Dos botones con el
  // mismo texto son lo normal, no la excepcion.
  const subirHtml = (archivo) => {
    if (!archivo) return;
    const lector = new FileReader();
    lector.onload = () => analizarHtml(String(lector.result));
    lector.readAsText(archivo, 'UTF-8');
  };

  const analizarHtml = (texto) => {
    api.post('/correo/html/analizar', { html: texto })
      .then(r => {
        setHtmlPropio(r.data.html);
        setEnlaces(r.data.enlaces || []);
        setDestinos({});
        setAvisoHtml(r.data.aviso || '');
        setAvanzado(false);
        toast.success(`HTML cargado. ${r.data.cuantos_enlaces} enlace(s) que puedes redirigir.`);
      })
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo leer el HTML'));
  };

  const [subiendoBloque, setSubiendoBloque] = useState(null);
  const subirImagenBloque = async (file, i) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) { toast.error('Selecciona una imagen'); return; }
    setSubiendoBloque(i);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBloques(bs => bs.map((x, j) => (j === i ? { ...x, url: data?.url || '' } : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo subir la imagen');
    } finally {
      setSubiendoBloque(null);
    }
  };

  const [magicAcciones, setMagicAcciones] = useState([]);
  const [nuevaMagic, setNuevaMagic] = useState(null);
  const [flowsDisponibles, setFlowsDisponibles] = useState([]);
  useEffect(() => { api.get('/whatsapp/flows/mine').then(r => setFlowsDisponibles(r.data?.flows || r.data?.items || (Array.isArray(r.data) ? r.data : []))).catch(() => {}); }, []);
  const [formMagic, setFormMagic] = useState({ nombre: '', modo: 'solo_lead', objetivo: '', texto: '', redirect_url: '', agent_name: '', company: '', template_name: '', template_lang: 'es', flow_id: '' });
  useEffect(() => { api.get('/correo/magic-actions').then(r => setMagicAcciones(r.data?.acciones || [])).catch(() => {}); }, []);
  const cargarMagic = () => api.get('/correo/magic-actions').then(r => setMagicAcciones(r.data?.acciones || [])).catch(() => {});
  const guardarMagic = (i) => {
    if (!formMagic.nombre.trim()) { toast.error('Ponle nombre a la acción'); return; }
    const params = {};
    if (formMagic.modo === 'voz_ia') { params.objetivo = formMagic.objetivo; params.agent_name = formMagic.agent_name; params.company = formMagic.company; }
    if (formMagic.modo === 'whatsapp') { params.template_name = formMagic.template_name; params.template_lang = formMagic.template_lang || 'es'; }
    if (formMagic.modo === 'flujo') { params.flow_id = formMagic.flow_id; }
    if (formMagic.redirect_url) params.redirect_url = formMagic.redirect_url;
    api.post('/correo/magic-actions', { nombre: formMagic.nombre, modo: formMagic.modo, params })
      .then(r => {
        toast.success('Acción guardada');
        const ac = r.data?.accion;
        cargarMagic();
        if (ac) setBloques(bs => bs.map((x, j) => j === i ? { ...x, accion_id: ac.id } : x));
        setNuevaMagic(null);
        setFormMagic({ nombre: '', modo: 'solo_lead', objetivo: '', texto: '', redirect_url: '', agent_name: '', company: '', template_name: '', template_lang: 'es', flow_id: '' });
      })
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo guardar'));
  };

  const guardarPlantilla = () => {
    const nombre = (nombrePlantilla || asunto || ('Plantilla ' + (plantillas.length + 1))).trim();
    api.post('/correo/plantillas', { nombre, asunto, bloques, html, fuente })
      .then(r => {
        toast.success(r.data.mensaje);
        setNombrePlantilla('');
        api.get('/correo/plantillas').then(x => setPlantillas(x.data?.plantillas || []));
      })
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo guardar'));
  };

  const usarPlantilla = (p) => {
    if (p.bloques?.length) { setBloques(p.bloques.map(b => ({ ...b }))); setHtmlPropio(''); setEnlaces([]); }
    else if (p.html) { analizarHtml(p.html); }
    if (p.asunto) setAsunto(p.asunto);
    if (p.fuente) setFuente(p.fuente);
    toast.success(`Cargada: ${p.nombre}`);
  };

  const cambiarAutomatismo = (tipo, cambios) => {
    const actual = automatismos?.[tipo] || {};
    api.put(`/correo/automatismos/${tipo}`, { ...actual, ...cambios })
      .then(r => {
        toast.success(r.data.mensaje);
        api.get('/correo/automatismos').then(x => setAutomatismos(x.data?.automatismos || null));
      })
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo guardar'));
  };

  // Pieza A: dominio REAL desde el que sale el mailing (display = realidad).
  const esEmpresaRemitente = remitenteSel === 'empresa';
  const dominioLanzamiento = (!esEmpresaRemitente && domSel && domSel.estado === 'verificado' && domSel.dominio)
    ? { dominio: domSel.dominio, propio: true }
    : { dominio: (domEmpresa && domEmpresa.dominio) ? domEmpresa.dominio : 'el dominio de tu empresa', propio: false };

  const mandar = (prueba) => {
    if (!asunto.trim()) { toast.error('Ponle asunto al correo'); setPaso('contenido'); return; }
    setEnviando(true);
    api.post('/correo/envios', { asunto, html, filtros, prueba, email_prueba: correoPrueba, programado_para: (!prueba && programar) ? cuando : '', destinos_cta: destinos, entity_type: esEmpresaRemitente ? '' : remitenteSel.split(':')[0], entity_id: esEmpresaRemitente ? '' : remitenteSel.split(':')[1] })
      .then(r => {
        if (r.data.programado_para) toast.success(r.data.mensaje);
        else if (r.data.es_prueba) toast.success('Prueba enviada. Revisa tu bandeja.');
        else toast.success(`Enviado a ${r.data.enviados}. Fallaron ${r.data.fallidos}.`);
        api.get('/correo/envios').then(x => setEnvios(x.data?.envios || []));
      })
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo enviar'))
      .finally(() => setEnviando(false));
  };

  const PASOS = [
    { id: 'audiencia', n: 1, titulo: 'A quién', icono: Users },
    { id: 'contenido', n: 2, titulo: 'Qué dice', icono: PenLine },
    { id: 'enviar', n: 3, titulo: 'Cuándo', icono: Send },
  ];

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-white flex items-center gap-2">
            <Mail className="w-6 h-6 text-sky-400" />Mailing SES-CEM
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Escríbele a tus leads. Primero eliges a quién, luego qué dice, y al final cuándo sale.
          </p>
        </div>
      </div>

      {correoListo === false && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-200/90">
            Todavía no has conectado tu cuenta de correo, así que no se puede enviar nada.
            Ve a <strong>Ajustes &gt; Integraciones &gt; Correo de tu empresa</strong>.
          </p>
        </div>
      )}

      <Tabs value={paso} onValueChange={setPaso}>
        <TabsList className="bg-black/20 border border-white/5">
          {PASOS.map(p => (
            <TabsTrigger key={p.id} value={p.id} className="text-xs gap-1.5" data-testid={`paso-${p.id}`}>
              <p.icono className="w-3.5 h-3.5" />{p.n}. {p.titulo}
            </TabsTrigger>
          ))}
          <TabsTrigger value="automatismos" className="text-xs gap-1.5" data-testid="paso-automatismos">
            <Zap className="w-3.5 h-3.5" />Automáticos
          </TabsTrigger>
          <TabsTrigger value="resultados" className="text-xs gap-1.5" data-testid="paso-resultados">
            <BarChart3 className="w-3.5 h-3.5" />Resultados
          </TabsTrigger>
        </TabsList>

        {/* ── PASO 1 ─────────────────────────────────────────────────────── */}
        <TabsContent value="audiencia" className="mt-4">
          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="bg-card/50 border-white/5 lg:col-span-2">
              <CardContent className="p-5 space-y-5">
                {!opciones ? (
                  <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : (
                  <>
                    <div>
                      <div className="flex items-center gap-1 mb-2">
                        <Label className="text-xs">Etapa del lead</Label>
                        <HelpTip title="Etapa" content={'En qué punto del proceso está cada persona.\n\nSi no marcas ninguna, entran todas. Marcar sirve para acotar: "solo prospectos" o "solo los que perdí", por ejemplo.'} />
                      </div>
                      {(opciones.status || []).length === 0 && (
                        <p className="text-[11px] text-muted-foreground">
                          Tus leads no tienen etapa capturada todavía, así que no hay nada por
                          lo que filtrar aquí. Se van a incluir todos.
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        {(opciones.status || []).map(o => (
                          <button key={o.valor} onClick={() => alternar('status', o.valor)}
                            data-testid={`filtro-status-${o.valor}`}
                            className={`px-2.5 py-1 rounded-full text-[11px] border transition ${
                              (filtros.status || []).includes(o.valor)
                                ? 'bg-sky-500/20 border-sky-500/40 text-sky-200'
                                : 'bg-black/20 border-white/10 text-muted-foreground hover:border-white/20'}`}>
                            {ETIQUETA_ESTADO[o.valor] || o.valor} <span className="opacity-60">{o.cuantos}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-1 mb-2">
                        <Label className="text-xs">De dónde llegó</Label>
                        <HelpTip title="Origen" content={'Por dónde entró el lead. Útil para hablarle distinto a quien llegó por WhatsApp que a quien llenó un formulario en tu web.'} />
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {(opciones.source || []).map(o => (
                          <button key={o.valor} onClick={() => alternar('source', o.valor)}
                            data-testid={`filtro-source-${o.valor}`}
                            className={`px-2.5 py-1 rounded-full text-[11px] border transition ${
                              (filtros.source || []).includes(o.valor)
                                ? 'bg-violet-500/20 border-violet-500/40 text-violet-200'
                                : 'bg-black/20 border-white/10 text-muted-foreground hover:border-white/20'}`}>
                            {ETIQUETA_ORIGEN[o.valor] || o.valor} <span className="opacity-60">{o.cuantos}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-1">
                          <Label className="text-xs">IA Score máximo</Label>
                          <HelpTip title="IA Score" content={'Qué tan caliente considera la IA a ese lead, del 0 al 10.\n\nPara retargeting te conviene lo BAJO: los de score alto ya están hablando con un vendedor y no necesitan un correo masivo.'} />
                        </div>
                        <Input type="number" min="0" max="10" placeholder="sin límite"
                          value={filtros.ai_score_max ?? ''} data-testid="filtro-score-max"
                          onChange={e => setFiltros(f => ({ ...f, ai_score_max: e.target.value === '' ? undefined : Number(e.target.value) }))}
                          className="bg-black/20 border-white/10" />
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-1">
                          <Label className="text-xs">Sin actividad desde hace (días)</Label>
                          <HelpTip title="Los que se enfriaron" content={'Deja fuera a quien se movió hace poco. Es el filtro de "los que no me contestaron": ponle 7 o 15 días.'} />
                        </div>
                        <Input type="number" min="1" placeholder="sin filtro"
                          value={filtros.sin_actividad_dias ?? ''} data-testid="filtro-sin-actividad"
                          onChange={e => setFiltros(f => ({ ...f, sin_actividad_dias: e.target.value === '' ? undefined : Number(e.target.value) }))}
                          className="bg-black/20 border-white/10" />
                      </div>
                    </div>

                    <div className="pt-3 border-t border-white/5 space-y-2">
                      <div className="flex items-center gap-1">
                        <Label className="text-xs">O sube tu propia lista</Label>
                        <HelpTip title="Cargar una lista" content={'Sube un CSV o Excel exportado con columnas de correo, nombre y telefono.\n\nAntes de usarla la limpiamos: corregimos los dedazos de dominios conocidos (gmial, hotmial, .con), quitamos duplicados quedandonos con el registro mas completo, y eliminamos lo que no es un correo.\n\nEl archivo NO se guarda en el servidor: se lee, se limpia y se tira. Solo se conservan los contactos ya limpios.'} />
                      </div>
                      <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/20 border border-dashed border-white/15 hover:border-sky-500/40 cursor-pointer transition">
                        <Upload className="w-4 h-4 text-sky-400" />
                        <span className="text-[11px] text-muted-foreground">
                          {subiendo ? 'Limpiando la lista...' : 'Elegir archivo CSV'}
                        </span>
                        <input type="file" accept=".csv,.txt,.tsv" className="hidden"
                          data-testid="subir-csv" disabled={subiendo}
                          onChange={e => cargarCsv(e.target.files?.[0])} />
                      </label>

                      {resumenCsv && (
                        <div className="p-3 rounded-lg bg-black/20 border border-white/5 space-y-1.5"
                             data-testid="resumen-csv">
                          <p className="text-xs text-white">
                            <strong>{resumenCsv.resumen.limpios}</strong> contactos limpios
                            de {resumenCsv.resumen.leidos} que trae el archivo
                          </p>
                          <div className="text-[11px] text-muted-foreground space-y-0.5">
                            {resumenCsv.resumen.corregidos > 0 && (
                              <p className="text-emerald-400">
                                {resumenCsv.resumen.corregidos} corregidos
                                {resumenCsv.ejemplos_corregidos?.[0] &&
                                  ` — ej: ${resumenCsv.ejemplos_corregidos[0].antes} → ${resumenCsv.ejemplos_corregidos[0].despues}`}
                              </p>
                            )}
                            {resumenCsv.resumen.eliminados > 0 && (
                              <p className="text-amber-400">
                                {resumenCsv.resumen.eliminados} eliminados por no ser correos validos
                              </p>
                            )}
                            {(resumenCsv.resumen.duplicados_por_correo + resumenCsv.resumen.duplicados_por_telefono) > 0 && (
                              <p>{resumenCsv.resumen.duplicados_por_correo + resumenCsv.resumen.duplicados_por_telefono} repetidos, se quedo el registro mas completo</p>
                            )}
                            {resumenCsv.dados_de_baja > 0 && (
                              <p>{resumenCsv.dados_de_baja} ya se habian dado de baja antes</p>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground/70">
                            El archivo no se guardo en el servidor. Solo los contactos limpios.
                          </p>
                        </div>
                      )}

                      {listas.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {listas.map(l => (
                            <button key={l.id} onClick={() => { setFiltros({ lista_id: l.id }); setResumenCsv(null); }}
                              className={`px-2.5 py-1 rounded-full text-[11px] border transition ${
                                filtros.lista_id === l.id
                                  ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-200'
                                  : 'bg-black/20 border-white/10 text-muted-foreground hover:border-white/20'}`}>
                              {l.nombre} <span className="opacity-60">{l.cuantos}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <button onClick={() => { setFiltros({ status: [], source: [] }); setResumenCsv(null); }}
                      className="text-[11px] text-muted-foreground hover:text-white underline">
                      Quitar todos los filtros
                    </button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* El contador en vivo */}
            <Card className="bg-card/50 border-white/5 h-fit sticky top-4" data-testid="contador-audiencia">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Van a recibir</p>
                  {contando && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                </div>
                <p className="text-4xl font-bold text-white font-heading" data-testid="contador-recibiran">
                  {conteo ? conteo.recibiran.toLocaleString() : '—'}
                </p>
                {conteo && (
                  <div className="space-y-1 text-[11px] text-muted-foreground">
                    <p>{conteo.contactos.toLocaleString()} contactos con correo</p>
                    {conteo.repetidos > 0 && <p>{conteo.repetidos} repetidos, se manda una vez</p>}
                    {conteo.dados_de_baja > 0 && (
                      <p className="text-amber-400">{conteo.dados_de_baja} se dieron de baja, no reciben</p>
                    )}
                    {conteo.pasa_del_tope && (
                      <p className="text-rose-400">Pasa del tope de {conteo.tope} por envío. Afina los filtros.</p>
                    )}
                  </div>
                )}
                {conteo?.muestra?.length > 0 && (
                  <div className="pt-2 border-t border-white/5 space-y-0.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Por ejemplo</p>
                    {conteo.muestra.slice(0, 3).map((m, i) => (
                      <p key={i} className="text-[11px] text-white/70 truncate">{m.nombre || m.email}</p>
                    ))}
                  </div>
                )}
                <Button onClick={() => setPaso('contenido')} disabled={!conteo?.recibiran}
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white" data-testid="ir-a-contenido">
                  Continuar
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── PASO 2 ─────────────────────────────────────────────────────── */}
        <TabsContent value="contenido" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="bg-card/50 border-white/5">
              <CardContent className="p-5 space-y-4">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1">
                    <Label className="text-xs">Asunto</Label>
                    <HelpTip title="El asunto" content={'Es lo único que ve antes de decidir si abre. Corto y concreto gana: "Tu cita del jueves" abre más que "Newsletter mensual".\n\nEvita MAYÚSCULAS y signos de más: son los que mandan el correo a spam.'} />
                  </div>
                  <Input value={asunto} onChange={e => setAsunto(e.target.value)}
                    placeholder="Ej: Sigue disponible el departamento que viste"
                    className="bg-black/20 border-white/10" data-testid="campo-asunto" />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs">Mis plantillas guardadas</Label>
                    <span className="text-[10px] text-muted-foreground">{plantillas.length} de {topePlantillas}</span>
                  </div>
                  {plantillas.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground italic">Aún no guardas ninguna. Arma tu correo abajo y dale "Guardar plantilla" (a la derecha) para reusarla aquí y en los Automáticos.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2" data-testid="repo-plantillas">
                      {plantillas.map(p => (
                        <div key={p.id} className="rounded-lg border border-white/10 bg-black/20 overflow-hidden">
                          <div className="h-24 bg-white overflow-hidden pointer-events-none">
                            <iframe title={p.nombre} srcDoc={p.html || construirHtml(p.bloques || [], p.fuente)}
                              className="origin-top-left" style={{ width: '200%', height: '200%', transform: 'scale(0.5)', border: 0 }} />
                          </div>
                          <div className="p-1.5 flex items-center justify-between gap-1">
                            <button onClick={() => usarPlantilla(p)} className="text-[11px] text-white hover:text-sky-300 truncate" data-testid="usar-plantilla">{p.nombre}</button>
                            <button onClick={() => api.delete(`/correo/plantillas/${p.id}`).then(() => {
                              toast.success('Plantilla borrada');
                              api.get('/correo/plantillas').then(x => setPlantillas(x.data?.plantillas || []));
                            })} className="text-muted-foreground/60 hover:text-rose-400 shrink-0">×</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-3 rounded-lg bg-black/20 border border-dashed border-white/15 space-y-2">
                  <div className="flex items-center gap-1">
                    <Label className="text-xs">¿Ya tienes tu propio HTML?</Label>
                    <HelpTip title="Subir tu HTML" content={'Si tu diseñador te entrego el correo hecho, subelo aqui.\n\nAl cargarlo detectamos todos los enlaces y botones para que puedas cambiar a donde va cada uno SIN tocar el codigo.\n\nPor seguridad quitamos scripts y marcos incrustados: ese correo sale firmado con tu dominio a miles de personas, y un script ahi dentro es un problema tuyo, no nuestro.'} />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <FileCode className="w-4 h-4 text-violet-400" />
                    <span className="text-[11px] text-muted-foreground">Elegir archivo .html</span>
                    <input type="file" accept=".html,.htm,.txt" className="hidden" data-testid="subir-html"
                      onChange={e => subirHtml(e.target.files?.[0])} />
                  </label>
                  {avisoHtml && <p className="text-[11px] text-amber-400">{avisoHtml}</p>}
                  {htmlPropio && (
                    <button onClick={() => { setHtmlPropio(''); setEnlaces([]); setDestinos({}); setAvisoHtml(''); }}
                      className="text-[11px] text-muted-foreground hover:text-white underline">
                      Quitar mi HTML y volver a los bloques
                    </button>
                  )}
                </div>

                {enlaces.length > 0 && (
                  <div className="space-y-2" data-testid="enlaces-html">
                    <div className="flex items-center gap-1">
                      <Label className="text-xs">A dónde llevan tus botones</Label>
                      <HelpTip title="Redirigir los enlaces" content={'Estos son los enlaces que encontramos en tu HTML. Cambia el destino del que quieras; los que dejes vacios se quedan como estaban.\n\nSirve para reusar el mismo diseño apuntando a otra propiedad, otra promocion u otro WhatsApp.'} />
                    </div>
                    {enlaces.map(e => (
                      <div key={e.id} className="p-2.5 rounded-lg bg-black/20 border border-white/5 space-y-1">
                        <p className="text-[11px] text-white truncate">{e.texto}</p>
                        <p className="text-[10px] text-muted-foreground truncate">ahora va a: {e.url || '(sin destino)'}</p>
                        <Input value={destinos[String(e.id)] || ''} placeholder="Dejar vacío = no se cambia"
                          onChange={ev => setDestinos(d => ({ ...d, [String(e.id)]: ev.target.value }))}
                          className="bg-black/20 border-white/10 text-xs" data-testid={`destino-${e.id}`} />
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <Label className="text-xs mb-2 block">Empieza de una plantilla</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {PLANTILLAS.map(p => (
                      <button key={p.id} onClick={() => { setBloques(p.bloques.map(b => ({ ...b }))); setAvanzado(false); }}
                        data-testid={`plantilla-${p.id}`}
                        className="p-2.5 rounded-lg bg-black/20 border border-white/10 hover:border-sky-500/40 text-left transition">
                        <p className="text-[11px] text-white font-medium">{p.nombre}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{p.pie}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {!avanzado ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs shrink-0">Tipografía</Label>
                      <select value={fuente} onChange={e => setFuente(e.target.value)}
                        className="flex-1 bg-black/20 border border-white/10 rounded-md text-xs px-2 py-1.5 text-white" data-testid="selector-fuente">
                        <option value="Arial, Helvetica, sans-serif">Arial (moderna)</option>
                        <option value="Georgia, 'Times New Roman', serif">Georgia (elegante)</option>
                        <option value="'Times New Roman', Times, serif">Times New Roman</option>
                        <option value="Verdana, Geneva, sans-serif">Verdana</option>
                        <option value="'Trebuchet MS', Helvetica, sans-serif">Trebuchet</option>
                        <option value="Tahoma, Geneva, sans-serif">Tahoma</option>
                        <option value="'Courier New', monospace">Courier (máquina)</option>
                      </select>
                    </div>
                    <Label className="text-xs">Contenido</Label>
                    {bloques.map((b, i) => (
                      <div key={i} className="p-2.5 rounded-lg bg-black/20 border border-white/5 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="text-[9px] border-white/10 capitalize">{b.tipo}</Badge>
                          <div className="flex items-center gap-1.5">
                            <button disabled={i === 0} title="Subir"
                              onClick={() => setBloques(bs => { const n = [...bs]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })}
                              className="text-muted-foreground hover:text-white disabled:opacity-25"><ChevronUp className="w-3.5 h-3.5" /></button>
                            <button disabled={i === bloques.length - 1} title="Bajar"
                              onClick={() => setBloques(bs => { const n = [...bs]; [n[i + 1], n[i]] = [n[i], n[i + 1]]; return n; })}
                              className="text-muted-foreground hover:text-white disabled:opacity-25"><ChevronDown className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setBloques(bs => bs.filter((_, j) => j !== i))}
                              className="text-muted-foreground hover:text-rose-400"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                        {(b.tipo === 'titulo' || b.tipo === 'texto') && (
                          <Textarea value={b.texto || ''} rows={b.tipo === 'texto' ? 3 : 1}
                            onChange={e => setBloques(bs => bs.map((x, j) => j === i ? { ...x, texto: e.target.value } : x))}
                            className="bg-black/20 border-white/10 text-xs" />
                        )}
                        {b.tipo === 'imagen' && (
                          <div className="space-y-1.5">
                            {b.url && <img src={b.url} alt="" className="max-h-28 rounded-md border border-white/10" />}
                            <div className="flex items-center gap-2">
                              <label className="cursor-pointer inline-flex items-center gap-1.5 text-[11px] text-sky-400 hover:text-sky-300 border border-dashed border-white/15 rounded-md px-3 py-2">
                                {subiendoBloque === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                                {b.url ? 'Cambiar imagen' : 'Subir imagen'}
                                <input type="file" accept="image/*" className="hidden" onChange={e => subirImagenBloque(e.target.files?.[0], i)} />
                              </label>
                              {b.url && <button onClick={() => setBloques(bs => bs.map((x, j) => j === i ? { ...x, url: '' } : x))} className="text-[11px] text-muted-foreground hover:text-rose-400">quitar</button>}
                            </div>
                            <Input value={b.destino || ''} placeholder="¿A dónde lleva al hacer clic? (opcional)"
                              onChange={e => setBloques(bs => bs.map((x, j) => j === i ? { ...x, destino: e.target.value } : x))}
                              className="bg-black/20 border-white/10 text-xs" />
                            <details><summary className="text-[10px] text-muted-foreground cursor-pointer">o pegar una URL de imagen</summary>
                              <Input value={b.url || ''} placeholder="https://..."
                                onChange={e => setBloques(bs => bs.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                                className="bg-black/20 border-white/10 text-xs mt-1" />
                            </details>
                            {b.url && (
                              <details>
                                <summary className="text-[10px] text-sky-400 cursor-pointer">Dibujar botón sobre la imagen (zonas clicables)</summary>
                                <div className="mt-1.5">
                                  <HotspotEditor url={b.url} hotspots={b.hotspots} magicAcciones={magicAcciones}
                                    onChange={hs => setBloques(bs => bs.map((x, j) => j === i ? { ...x, hotspots: hs } : x))} />
                                </div>
                              </details>
                            )}
                          </div>
                        )}
                        {b.tipo === 'video' && (
                          <Input value={b.url || ''} placeholder="Pega el link de YouTube"
                            onChange={e => setBloques(bs => bs.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                            className="bg-black/20 border-white/10 text-xs" />
                        )}
                        {b.tipo === 'boton' && (
                          <div className="grid grid-cols-2 gap-1.5">
                            <Input value={b.texto || ''} placeholder="Texto del botón"
                              onChange={e => setBloques(bs => bs.map((x, j) => j === i ? { ...x, texto: e.target.value } : x))}
                              className="bg-black/20 border-white/10 text-xs" />
                            <Input value={b.url || ''} placeholder="A dónde lleva"
                              onChange={e => setBloques(bs => bs.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                              className="bg-black/20 border-white/10 text-xs" />
                          </div>
                        )}
                        {b.tipo === 'magic' && (
                          <div className="space-y-1.5">
                            <Input value={b.texto || ''} placeholder="Texto del botón (ej: Quiero que me llamen)"
                              onChange={e => setBloques(bs => bs.map((x, j) => j === i ? { ...x, texto: e.target.value } : x))}
                              className="bg-black/20 border-white/10 text-xs" />
                            <div className="flex items-center gap-1.5">
                              <select value={b.accion_id || ''}
                                onChange={e => setBloques(bs => bs.map((x, j) => j === i ? { ...x, accion_id: e.target.value } : x))}
                                className="flex-1 bg-black/20 border border-white/10 rounded-md text-xs px-2 py-2 text-white" data-testid={`magic-accion-${i}`}>
                                <option value="">— Elige qué hace el botón —</option>
                                {magicAcciones.map(ac => <option key={ac.id} value={ac.id}>{ac.nombre}</option>)}
                              </select>
                              <button type="button" onClick={() => setNuevaMagic(nm => nm === i ? null : i)}
                                className="text-[11px] text-sky-400 hover:text-sky-300 border border-white/10 rounded-md px-2 py-2 shrink-0">+ Nueva</button>
                            </div>
                            {nuevaMagic === i && (
                              <div className="p-2.5 rounded-lg bg-black/30 border border-dashed border-white/15 space-y-1.5" data-testid="magic-config">
                                <Input value={formMagic.nombre} placeholder="Nombre de la acción (ej: Llamada inmediata)"
                                  onChange={e => setFormMagic(f => ({ ...f, nombre: e.target.value }))} className="bg-black/20 border-white/10 text-xs" />
                                <div className="grid grid-cols-2 gap-1.5" data-testid="magic-modos">
                                  {[['solo_lead', 'Solo lead'], ['voz_ia', 'Llamada IA'], ['whatsapp', 'WhatsApp'], ['flujo', 'Activar flujo']].map(([v, l]) => (
                                    <button key={v} type="button" onClick={() => setFormMagic(f => ({ ...f, modo: v }))}
                                      className={`text-[11px] rounded-md px-2 py-2 border ${formMagic.modo === v ? 'border-sky-500/50 bg-sky-500/15 text-white' : 'border-white/10 bg-black/20 text-muted-foreground hover:text-white'}`}>{l}</button>
                                  ))}
                                </div>
                                {formMagic.modo === 'voz_ia' && (
                                  <>
                                    <div className="grid grid-cols-2 gap-1.5">
                                      <Input value={formMagic.agent_name} placeholder="Nombre con que se presenta (ej: Ana)"
                                        onChange={e => setFormMagic(f => ({ ...f, agent_name: e.target.value }))} className="bg-black/20 border-white/10 text-xs" />
                                      <Input value={formMagic.company} placeholder="Empresa (ej: Casas KREA)"
                                        onChange={e => setFormMagic(f => ({ ...f, company: e.target.value }))} className="bg-black/20 border-white/10 text-xs" />
                                    </div>
                                    <Textarea value={formMagic.objetivo} rows={2} placeholder="Objetivo de la llamada (ej: agenda una visita al depto que vio)"
                                      onChange={e => setFormMagic(f => ({ ...f, objetivo: e.target.value }))} className="bg-black/20 border-white/10 text-xs" />
                                  </>
                                )}
                                {formMagic.modo === 'whatsapp' && (
                                  <>
                                    <div className="grid grid-cols-2 gap-1.5">
                                      <Input value={formMagic.template_name} placeholder="Nombre de la plantilla aprobada"
                                        onChange={e => setFormMagic(f => ({ ...f, template_name: e.target.value }))} className="bg-black/20 border-white/10 text-xs" />
                                      <Input value={formMagic.template_lang} placeholder="Idioma (ej: es, es_MX)"
                                        onChange={e => setFormMagic(f => ({ ...f, template_lang: e.target.value }))} className="bg-black/20 border-white/10 text-xs" />
                                    </div>
                                    <p className="text-[10px] text-amber-200/80">El WhatsApp de salida debe ser una <strong>plantilla aprobada por Meta</strong> (un texto libre no se entrega fuera de las 24h). Por ahora usa una sin variables.</p>
                                  </>
                                )}
                                {formMagic.modo === 'flujo' && (
                                  <>
                                    <select value={formMagic.flow_id} onChange={e => setFormMagic(f => ({ ...f, flow_id: e.target.value }))} className="w-full bg-black/20 border border-white/10 rounded-md text-xs px-2 py-2 text-white" data-testid="magic-flow-select">
                                      <option value="">Elige el Flow de WhatsApp a enviar…</option>
                                      {flowsDisponibles.map(fl => { const _v = fl.flow_id || fl.id || fl._id || ''; return <option key={_v} value={_v}>{fl.name || fl.nombre || fl.titulo || _v}</option>; })}
                                    </select>
                                    <p className="text-[10px] text-muted-foreground">Al hacer clic: se envía este Flow al lead, y tras el <strong>delay del IVR</strong> se detona la llamada en <strong>conferencia con el vendedor de turno</strong>. El lead queda en tu mailing.</p>
                                  </>
                                )}
                                <Input value={formMagic.redirect_url} placeholder="A dónde mandar al lead tras el clic (opcional)"
                                  onChange={e => setFormMagic(f => ({ ...f, redirect_url: e.target.value }))} className="bg-black/20 border-white/10 text-xs" />
                                <Button size="sm" className="w-full bg-sky-500 hover:bg-sky-600 text-white text-xs" onClick={() => guardarMagic(i)} data-testid="magic-guardar">Guardar acción</Button>
                              </div>
                            )}
                            <p className="text-[10px] text-muted-foreground">Al hacer clic, el contacto queda como lead y se dispara la acción.</p>
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="flex flex-wrap gap-1.5">
                      {['titulo', 'texto', 'imagen', 'video', 'boton', 'magic'].map(t => (
                        <Button key={t} variant="outline" size="sm"
                          onClick={() => setBloques(bs => [...bs, { tipo: t, texto: '', url: '' }])}
                          className="border-white/10 text-[11px] capitalize" data-testid={`agregar-${t}`}>
                          <Plus className="w-3 h-3 mr-1" />{t}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label className="text-xs">HTML</Label>
                    <Textarea value={htmlCrudo} onChange={e => setHtmlCrudo(e.target.value)} rows={14}
                      className="bg-black/20 border-white/10 font-mono text-[11px]" data-testid="html-crudo" />
                  </div>
                )}

                <button onClick={() => { setAvanzado(a => !a); if (!avanzado) setHtmlCrudo(construirHtml(bloques)); }}
                  className="text-[11px] text-muted-foreground hover:text-white underline" data-testid="alternar-avanzado">
                  {avanzado ? 'Volver a bloques' : 'Modo avanzado (editar el HTML)'}
                </button>
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-white/5">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold flex items-center gap-1">
                    <Eye className="w-3 h-3" />Así se va a ver
                  </p>
                  <div className="flex gap-1">
                    <button onClick={() => setVista('escritorio')}
                      className={`p-1.5 rounded ${vista === 'escritorio' ? 'bg-white/10 text-white' : 'text-muted-foreground'}`}>
                      <Monitor className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setVista('movil')} data-testid="vista-movil"
                      className={`p-1.5 rounded ${vista === 'movil' ? 'bg-white/10 text-white' : 'text-muted-foreground'}`}>
                      <Smartphone className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-white/10 bg-white mx-auto transition-all"
                  style={{ maxWidth: vista === 'movil' ? 375 : '100%' }}>
                  <iframe title="vista previa" srcDoc={html} className="w-full" style={{ height: 460, border: 0 }} />
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Al final de cada correo se agrega solo el enlace para darse de baja. Es obligatorio por ley
                  y es lo que evita que te marquen como spam.
                </p>
                <div className="flex gap-2 pt-2 border-t border-white/5">
                  <Input value={nombrePlantilla} onChange={e => setNombrePlantilla(e.target.value)}
                    placeholder="Nombre para guardarla" className="bg-black/20 border-white/10 text-xs"
                    data-testid="nombre-plantilla" />
                  <Button variant="outline" onClick={guardarPlantilla}
                    className="border-white/10 text-xs shrink-0" data-testid="guardar-plantilla">
                    <Save className="w-3 h-3 mr-1" />Guardar plantilla
                  </Button>
                </div>
                <Button onClick={() => setPaso('enviar')} className="w-full bg-sky-500 hover:bg-sky-600 text-white"
                  data-testid="ir-a-enviar">Continuar</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── PASO 3 ─────────────────────────────────────────────────────── */}
        <TabsContent value="enviar" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="bg-card/50 border-white/5">
              <CardContent className="p-5 space-y-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Antes de mandarlo a todos</p>
                <div className="space-y-1.5">
                  <Label className="text-xs">Mándate una prueba</Label>
                  <div className="flex gap-2">
                    <Input value={correoPrueba} onChange={e => setCorreoPrueba(e.target.value)}
                      placeholder="tu@correo.com" className="bg-black/20 border-white/10"
                      data-testid="correo-prueba" />
                    <Button variant="outline" onClick={() => mandar(true)} disabled={enviando}
                      className="border-white/10 shrink-0" data-testid="enviar-prueba">
                      {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar prueba'}
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Llega solo a esa dirección. Nadie de tu lista se entera.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-white/5">
              <CardContent className="p-5 space-y-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Mandar de verdad</p>
                <div className="p-3 rounded-lg bg-black/20 border border-white/5 space-y-1">
                  <p className="text-sm text-white">
                    <strong>{conteo?.recibiran?.toLocaleString() || 0}</strong> personas van a recibir
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">Asunto: {asunto || '(sin asunto)'}</p>
                  {conteo?.dados_de_baja > 0 && (
                    <p className="text-[11px] text-amber-400">{conteo.dados_de_baja} dados de baja quedan fuera</p>
                  )}
                </div>
                <div className="p-3 rounded-lg bg-black/20 border border-white/5 space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-white">Remitente de este mailing</Label>
                    <HelpTip title="¿Desde qué dominio sale?" content={'Por defecto el mailing sale desde el dominio de tu empresa.\n\nSi lo mandas a nombre de una marca o desarrollo con su PROPIO dominio verificado, elígelo aquí y saldrá firmado con ESE dominio.\n\nSi la marca/desarrollo no tiene dominio propio verificado, el correo igual sale — desde el dominio de tu empresa. Nunca se cae un envío por esto.'} />
                  </div>
                  <Select value={remitenteSel} onValueChange={setRemitenteSel}>
                    <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="remitente-selector">
                      <SelectValue placeholder="Mi empresa (predeterminado)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="empresa">Mi empresa (predeterminado)</SelectItem>
                      {licencias.map(l => (
                        <SelectItem key={`${l.entity_type}:${l.entity_id}`} value={`${l.entity_type}:${l.entity_id}`}>
                          {l.nombre} {l.entity_type === 'brand' ? '(marca)' : '(desarrollo)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    Se enviará desde: <span className="text-white font-medium">{dominioLanzamiento.dominio}</span>
                    {!esEmpresaRemitente && !dominioLanzamiento.propio && (
                      <span className="text-amber-400"> — esta marca/desarrollo no tiene dominio verificado; sale del de tu empresa</span>
                    )}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-black/20 border border-white/5 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={programar} data-testid="activar-programado"
                      onChange={e => setProgramar(e.target.checked)}
                      className="accent-sky-500" />
                    <span className="text-xs text-white">Programarlo para después</span>
                    <HelpTip title="Programar" content={'Elige el dia y la hora y el correo sale solo, sin que tengas que estar aqui.\n\nLa hora es la de tu equipo. Mientras no salga lo puedes cancelar desde Resultados; una vez enviado ya no.\n\nConsejo: martes a jueves entre 9 y 11 de la mañana es cuando mas se abren los correos.'} />
                  </label>
                  {programar && (
                    <Input type="datetime-local" value={cuando} data-testid="fecha-programada"
                      onChange={e => setCuando(e.target.value)}
                      className="bg-black/20 border-white/10 text-xs" />
                  )}
                </div>
                <Button onClick={() => mandar(false)}
                  disabled={enviando || !conteo?.recibiran || !asunto.trim() || correoListo === false || (programar && !cuando)}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="enviar-campana">
                  {enviando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  {programar ? 'Programar' : 'Enviar'} a {conteo?.recibiran?.toLocaleString() || 0}
                </Button>
                <p className="text-[10px] text-muted-foreground">
                  Sale con tu dominio y se cobra a tu cuenta de correo. Una vez enviado no se puede deshacer.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── AUTOMATISMOS ───────────────────────────────────────────────── */}
        <TabsContent value="automatismos" className="mt-4">
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Correos que salen solos cuando pasa algo. Al encender uno, cuenta desde ese
              momento: a los leads que ya tenías <strong>no</strong> se les escribe.
            </p>

            {[
              { tipo: 'nuevo_lead', titulo: 'Nuevo lead',
                pie: 'Sale en cuanto entra un lead con correo, sin importar por dónde llegó.',
                ayuda: 'El primer correo es el que más se abre: la persona acaba de dejarte sus datos y te tiene presente.\n\nÚsalo para confirmar que recibiste su solicitud y decirle qué sigue. No para venderle todavía.' },
              { tipo: 'cita_confirmada', titulo: 'Confirmación de cita',
                pie: 'Sale cuando la IA agenda una cita con el lead.',
                ayuda: 'Reduce las citas a las que nadie llega. Le queda por escrito el día y la hora, y puede responder si necesita moverla.\n\nSe dispara con la cita que agenda la IA por WhatsApp.' },
              { tipo: 'nurturing', titulo: 'Nurturing — 5 pasos',
                pie: 'Secuencia de 5 correos para el que no contesta o tiene IA Score bajo.',
                ayuda: 'Cinco correos espaciados a lo largo de un mes: día 0, 3, 7, 14 y 30.\n\nEs para recuperar al que se enfrió, no para insistirle a quien ya está hablando con un vendedor. Por eso conviene ponerle un IA Score máximo bajo.\n\nManda UN correo por persona por ciclo, nunca la secuencia de golpe.' },
            ].map(a => {
              const cfg = automatismos?.[a.tipo] || {};
              return (
                <Card key={a.tipo} className="bg-card/50 border-white/5" data-testid={`auto-${a.tipo}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm text-white font-medium">{a.titulo}</p>
                          <HelpTip title={a.titulo} content={a.ayuda} />
                          {cfg.activo
                            ? <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-400">encendido</Badge>
                            : <Badge variant="outline" className="text-[10px] border-white/10 text-muted-foreground">apagado</Badge>}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{a.pie}</p>
                      </div>
                      <Button size="sm" variant={cfg.activo ? 'outline' : 'default'}
                        onClick={() => cambiarAutomatismo(a.tipo, { activo: !cfg.activo })}
                        disabled={correoListo === false}
                        className={cfg.activo ? 'border-white/10 text-xs shrink-0' : 'bg-emerald-500 hover:bg-emerald-600 text-white text-xs shrink-0'}
                        data-testid={`toggle-${a.tipo}`}>
                        {cfg.activo ? 'Apagar' : 'Encender'}
                      </Button>
                    </div>

                    {cfg.activo && (
                      <div className="space-y-2 pt-2 border-t border-white/5">
                        <Input value={cfg.asunto || ''} placeholder="Asunto del correo"
                          onChange={e => setAutomatismos(x => ({ ...x, [a.tipo]: { ...cfg, asunto: e.target.value } }))}
                          onBlur={() => cambiarAutomatismo(a.tipo, { asunto: cfg.asunto })}
                          className="bg-black/20 border-white/10 text-xs" data-testid={`asunto-${a.tipo}`} />
                        {a.tipo !== 'nurturing' && (
                          <div className="space-y-1">
                            <Label className="text-[11px] text-muted-foreground">Qué dice — elige una plantilla que armaste en "Qué dice"</Label>
                            <select value={cfg.plantilla_id || ''}
                              onChange={e => {
                                const v = e.target.value;
                                if (!v) { cambiarAutomatismo(a.tipo, { plantilla_id: '', html: '' }); return; }
                                if (v.startsWith('p:')) {
                                  const pre = PLANTILLAS.find(x => x.id === v.slice(2));
                                  if (pre) { const t = (pre.bloques.find(b => b.tipo === 'titulo') || {}).texto; cambiarAutomatismo(a.tipo, { plantilla_id: v, asunto: cfg.asunto || t || pre.nombre, html: construirHtml(pre.bloques) }); }
                                } else {
                                  const p = plantillas.find(x => x.id === v);
                                  if (p) cambiarAutomatismo(a.tipo, { plantilla_id: p.id, asunto: p.asunto || cfg.asunto || '', html: p.html || construirHtml(p.bloques || []) });
                                }
                              }}
                              className="w-full bg-black/20 border border-white/10 rounded-md text-xs px-2 py-2 text-white" data-testid={`plantilla-auto-${a.tipo}`}>
                              <option value="">Texto simple de cortesía (por defecto)</option>
                              {plantillas.length > 0 && <optgroup label="Mis plantillas guardadas">{plantillas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}</optgroup>}
                              <optgroup label="Plantillas de arranque">{PLANTILLAS.map(p => <option key={p.id} value={'p:' + p.id}>{p.nombre}</option>)}</optgroup>
                            </select>
                            <p className="text-[10px] text-muted-foreground">
                              {cfg.html ? '✓ Este automático usa el diseño de una plantilla.' : 'Arma un correo en "Qué dice", guárdalo como plantilla y aquí elígelo.'}
                            </p>
                          </div>
                        )}
                        {a.tipo === 'nurturing' && (
                          <div className="grid grid-cols-2 gap-2">
                            <Input type="number" min="0" max="10" placeholder="IA Score máximo"
                              value={cfg.condiciones?.ai_score_max ?? ''}
                              onChange={e => setAutomatismos(x => ({ ...x, nurturing: { ...cfg, condiciones: { ...(cfg.condiciones || {}), ai_score_max: e.target.value === '' ? undefined : Number(e.target.value) } } }))}
                              onBlur={() => cambiarAutomatismo('nurturing', { condiciones: cfg.condiciones })}
                              className="bg-black/20 border-white/10 text-xs" />
                            <Input type="number" min="1" placeholder="Días sin actividad"
                              value={cfg.condiciones?.sin_actividad_dias ?? ''}
                              onChange={e => setAutomatismos(x => ({ ...x, nurturing: { ...cfg, condiciones: { ...(cfg.condiciones || {}), sin_actividad_dias: e.target.value === '' ? undefined : Number(e.target.value) } } }))}
                              onBlur={() => cambiarAutomatismo('nurturing', { condiciones: cfg.condiciones })}
                              className="bg-black/20 border-white/10 text-xs" />
                          </div>
                        )}
                        {a.tipo === 'nurturing' && etapas.length > 0 && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] text-muted-foreground">Arranca cuando el lead</span>
                            <select
                              value={cfg.ancla_etapa || ''}
                              onChange={e => {
                                const v = e.target.value;
                                setAutomatismos(x => ({ ...x, nurturing: { ...(x?.nurturing || {}), ancla_etapa: v } }));
                                cambiarAutomatismo('nurturing', { ancla_etapa: v });
                              }}
                              className="h-6 bg-black/20 border border-white/10 rounded text-xs px-1"
                              data-testid="nurt-ancla-etapa">
                              <option value="">entra (desde que llega el lead)</option>
                              {etapas.map(stg => (
                                <option key={stg.id} value={stg.id}>llega a: {stg.name || stg.nombre}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {cfg.activado_en && (
                          <p className="text-[10px] text-muted-foreground">
                            Cuenta desde el {String(cfg.activado_en).slice(0, 16).replace('T', ' ')}
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {pasosSugeridos.length > 0 && (
              <Card className="bg-card/50 border-white/5" data-testid="nurturing-editor">
                <CardContent className="p-4 space-y-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">
                    La secuencia de nurturing — edítala
                  </p>
                  {(automatismos?.nurturing?.pasos?.length ? automatismos.nurturing.pasos : pasosSugeridos).map((p, i) => {
                    const base = () => (automatismos?.nurturing?.pasos?.length ? automatismos.nurturing.pasos : pasosSugeridos);
                    const guardar = () => cambiarAutomatismo('nurturing', { pasos: base().map(x => ({ ...x })) });
                    const setPaso = (campo, valor) => setAutomatismos(x => {
                      const b = x?.nurturing?.pasos?.length ? x.nurturing.pasos : pasosSugeridos;
                      return { ...x, nurturing: { ...(x?.nurturing || {}), pasos: b.map((q, j) => j === i ? { ...q, [campo]: valor } : { ...q }) } };
                    });
                    const aplicar = (campos) => {
                      const nuevos = base().map((q, j) => j === i ? { ...q, ...campos } : { ...q });
                      setAutomatismos(x => ({ ...x, nurturing: { ...(x?.nurturing || {}), pasos: nuevos } }));
                      cambiarAutomatismo('nurturing', { pasos: nuevos });
                    };
                    // Fase 1: separacion de dias editable. paso.dias = dias ABSOLUTOS desde el arranque
                    // (lo que lee el worker). El usuario edita la SEPARACION; recalculamos los absolutos.
                    const cur = base();
                    const sep = i > 0 ? Math.max(0, Number(p.dias || 0) - Number(cur[i - 1]?.dias || 0)) : Number(p.dias || 0);
                    const setSeparacion = (val) => {
                      const nv = Math.max(0, parseInt(val, 10) || 0);
                      const c = base();
                      const seps = c.map((x, j) => j > 0 ? Math.max(0, Number(x.dias || 0) - Number(c[j - 1].dias || 0)) : Number(x.dias || 0));
                      seps[i] = nv;
                      let acc = 0;
                      const arr = c.map((x, j) => { acc += seps[j]; return { ...x, dias: acc }; });
                      setAutomatismos(x => ({ ...x, nurturing: { ...(x?.nurturing || {}), pasos: arr } }));
                      cambiarAutomatismo('nurturing', { pasos: arr }); // persistir valor fresco (no depender de onBlur con closure viejo)
                    };
                    return (
                      <div key={i} className="p-2.5 rounded-lg bg-black/20 border border-white/5 space-y-1.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] text-muted-foreground">{i === 0 ? 'Arranca' : 'Luego de'}</span>
                          <Input type="number" min="0" value={sep}
                            onChange={e => setSeparacion(e.target.value)}
                            className="h-6 w-14 bg-black/20 border-white/10 text-xs text-center px-1" data-testid={`nurt-dias-${i}`} />
                          <span className="text-[10px] text-muted-foreground">{i === 0 ? (automatismos?.nurturing?.ancla_etapa ? 'días desde que llega a la etapa' : 'días desde que el lead entra') : 'días del paso anterior'}</span>
                          <span className="text-[10px] text-white/40 ml-auto">{p.dias === 0 ? 'sale al instante' : `sale el día ${p.dias}`}</span>
                        </div>
                        <Input value={p.asunto || ''} placeholder="Asunto de este paso"
                          onChange={e => setPaso('asunto', e.target.value)} onBlur={guardar}
                          className="bg-black/20 border-white/10 text-xs" data-testid={`nurt-asunto-${i}`} />
                        <select value={p.plantilla_id || ''}
                          onChange={e => {
                            const v = e.target.value;
                            if (!v) { aplicar({ plantilla_id: '', html: '' }); return; }
                            if (v.startsWith('p:')) { const pre = PLANTILLAS.find(x => x.id === v.slice(2)); if (pre) aplicar({ plantilla_id: v, html: construirHtml(pre.bloques) }); }
                            else { const pl = plantillas.find(x => x.id === v); if (pl) aplicar({ plantilla_id: pl.id, html: pl.html || construirHtml(pl.bloques || []) }); }
                          }}
                          className="w-full bg-black/20 border border-white/10 rounded-md text-xs px-2 py-2 text-white" data-testid={`nurt-plantilla-${i}`}>
                          <option value="">Texto simple (escríbelo abajo)</option>
                          {plantillas.length > 0 && <optgroup label="Mis plantillas">{plantillas.map(pl => <option key={pl.id} value={pl.id}>{pl.nombre}</option>)}</optgroup>}
                          <optgroup label="Con diseño (imagen/botón)">{PLANTILLAS.map(pl => <option key={pl.id} value={'p:' + pl.id}>{pl.nombre}</option>)}</optgroup>
                        </select>
                        {p.html
                          ? <p className="text-[10px] text-emerald-300/80">✓ Usa una plantilla con diseño (imagen, botón…). <button onClick={() => aplicar({ plantilla_id: '', html: '' })} className="underline hover:text-white">quitar</button></p>
                          : <Textarea value={p.texto || ''} rows={2} placeholder="Qué dice este correo"
                              onChange={e => setPaso('texto', e.target.value)} onBlur={guardar}
                              className="bg-black/20 border-white/10 text-xs" />}
                      </div>
                    );
                  })}
                  <p className="text-[10px] text-muted-foreground">
                    Un correo por persona por ciclo; quien se da de baja sale al instante. Cambia el texto y se guarda solo.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ── RESULTADOS ─────────────────────────────────────────────────── */}
        <TabsContent value="resultados" className="mt-4">
          <Card className="bg-card/50 border-white/5">
            <CardContent className="p-5">
              {envios.length === 0 ? (
                <div className="text-center py-10">
                  <BarChart3 className="w-10 h-10 mx-auto text-muted-foreground/20 mb-3" />
                  <p className="text-sm text-muted-foreground">Todavía no has mandado ningún correo.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {envios.map(e => (
                    <div key={e.id} className="p-3 rounded-lg bg-black/20 border border-white/5 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{e.asunto}</p>
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                          {e.estado === 'programado' && <Clock className="w-3 h-3 text-amber-400" />}
                          {e.estado === 'programado'
                            ? `sale el ${String(e.programado_para || '').slice(0, 16).replace('T', ' a las ')}`
                            : String(e.creado_en || '').slice(0, 16).replace('T', ' ')}
                          {e.proveedor ? ` · ${e.proveedor}` : ''}{e.es_prueba && ' · prueba'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <p className="text-sm text-emerald-400 font-mono">{e.enviados ?? 0}</p>
                          <p className="text-[9px] text-muted-foreground uppercase">enviados</p>
                        </div>
                        {(e.fallidos ?? 0) > 0 && (
                          <div className="text-right">
                            <p className="text-sm text-rose-400 font-mono">{e.fallidos}</p>
                            <p className="text-[9px] text-muted-foreground uppercase">fallaron</p>
                          </div>
                        )}
                        {e.estado === 'programado' ? (
                          <button onClick={() => {
                            api.delete(`/correo/envios/${e.id}`)
                              .then(r => { toast.success(r.data.mensaje); api.get('/correo/envios').then(x => setEnvios(x.data?.envios || [])); })
                              .catch(err => toast.error(err?.response?.data?.detail || 'No se pudo cancelar'));
                          }} className="text-[10px] text-amber-400 hover:text-amber-300 underline"
                             data-testid="cancelar-programado">
                            cancelar
                          </button>
                        ) : e.estado === 'enviado'
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          : <AlertTriangle className="w-4 h-4 text-amber-400" />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-muted-foreground mt-4">
                Aperturas, clics y rebotes aparecen aquí en cuanto se conecten los avisos del proveedor
                (siguiente entrega).
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
